import { useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import {
  getDefaultForm,
  getSubmitResolvers,
  removeUnitItem
} from '@aytm/app/stats/page/question-views/vk/components/views/chart/components/chart-buttons/lib/utils'

export const useChartButtonsForm = ({ answer, config, onModelChange }) => {
  const [active, setActive] = useState(null)
  const [form, setForm] = useState(getDefaultForm({ answer, config }))
  const submitResolvers = getSubmitResolvers({ config, onModelChange })

  // The block seeds its pricing config again once a real answer lands, which can be after these
  // buttons mount -- a form seeded only at mount kept the empty config and drew no cost lines. It
  // follows the config for as long as nothing is being edited, so a reader's edit is never replaced.
  useEffect(() => {
    if (null === active) setForm(getDefaultForm({ answer, config }))
  }, [answer, config]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateGroup = (group, updater) => {
    setForm(prev => {
      const prevGroup = prev[group]
      return {
        ...prev,
        [group]: {
          ...prevGroup,
          isTouched: true,
          value: typeof updater === 'function' ? updater(prevGroup.value) : updater
        }
      }
    })
  }

  const setGroupTouched = (group, isTouched) => {
    setForm(prev => {
      return {
        ...prev,
        [group]: {
          ...prev[group],
          isTouched
        }
      }
    })
  }

  const handleAddCostItem = () => {
    // empty, so the line shows its placeholders until the reader types a cost and a quantity
    updateGroup('cost', prevList => [...prevList, { id: uuidv4(), value: '', quantity: '' }])
  }

  // Removing a line is an edit like any other: it is sent with Update Cost, not on the click
  const handleRemoveCostItem = id => {
    setForm(prev => {
      return {
        ...prev,
        cost: {
          ...prev.cost,
          value: removeUnitItem(prev.cost.value, id),
          isTouched: true
        }
      }
    })
  }

  const initSaveForm = (name, value) => {
    const resolver = submitResolvers?.[name]

    resolver(value ?? form?.[name]?.value)
    setActive(null)
    setGroupTouched(name, false)
  }

  const handleClick = event => {
    const name = event.target.name
    if (name === active && form?.[name]?.isTouched) {
      initSaveForm(name)
    } else {
      setForm({
        ...getDefaultForm({ answer, config }),
        cost: form.cost // remeber cost as was in original
      })
      setGroupTouched('cost', false)
      setActive(prevValue => (prevValue === name ? null : name))
    }
  }

  const handleInputChange = event => {
    const { name, value } = event.target

    updateGroup(name, value)
  }

  // Taken as the state to move to: the box reports its own `checked`, but the label beside it
  // answers a plain click, which carries neither a name nor a checked state of its own.
  const setCheckbox = (name, value) => {
    updateGroup(name, value)
    initSaveForm(name, value)
  }

  const handleCheckboxChange = event => setCheckbox(event.target.name, event.checked)

  const handleInputKeyPress = event => {
    const { name } = event.target

    if (event.key === 'Enter') {
      initSaveForm(name)
    }
  }

  const handleNestedInputChange = (group, event, id) => {
    const { name, value } = event.target
    updateGroup(group, prevList => prevList.map(item => (item.id === id ? { ...item, [name]: value } : item)))
  }

  return {
    active,
    form,
    handleClick,
    handleAddCostItem,
    handleRemoveCostItem,
    handleInputChange,
    handleCheckboxChange,
    setCheckbox,
    handleInputKeyPress,
    handleNestedInputChange
  }
}
